import { Button, Drawer, Row, Space, message } from "antd";
import styles from "./index.less";
import { Fragment, useRef, useState } from "react";
import {
  AskAIType,
  StreamResponseDataType,
  streamFetch,
} from "../FlowEditPage/utils/streamFetch";
import { nanoid } from "nanoid";
import { produce } from "immer";
import { ReactComponent as ClearIcon } from "../assets/clear.svg";
import ChatItemHuman from "../ChatDrawer/ChatItems/ChatItemHuman";
import ChatItemAI from "../ChatDrawer/ChatItems/ChatItemAI";
import ChatInput from "../ChatDrawer/ChatInput";
import { clearN8nTestHistoryService } from "./services/n8nTest";
import { ResponseCode } from "../constants";
import { FlowNodeTypeEnum } from "@packages/flow-core/module/node/constants";
import {
  CardMessage,
  ChatMessageType,
  FileMessage,
  ImageMessage,
  MiniProgramMessage,
  TextMessage,
  VideoMessage,
  VoiceMessage,
} from "../ChatDrawer/types";
import classNames from "classnames";

type Role = "human" | "ai";

type N8nChatDrawerProps = {
  visible: boolean;
  onClose: () => void;
  /** n8n 编排机器人 ID */
  _id: string;
  interruptOldOnNew?: boolean;
  className?: string;
};

type ChatHistory = {
  msg?: ChatMessageType;
  status: "running" | "done";
  role: Role;
  id: string;
};

/** n8n 运行期失败无任何事件通道，前端能收到的产出事件仅限对话产出与触发失败两类 */
type N8nErrorEvent = {
  errorMessage?: string;
  dialog?: boolean;
};

/**
 * SSE 产出 flowType → AI 气泡消息的映射表
 * 新增产出类型在此登记一行；未登记的类型（如 mailOutput）跳过不渲染
 */
const OUTPUT_MSG_BUILDERS: Partial<
  Record<FlowNodeTypeEnum, (outputMap: any) => ChatMessageType>
> = {
  [FlowNodeTypeEnum.messageOutput]: (outputMap: TextMessage) => ({
    ...outputMap,
    type: "text",
  }),
  [FlowNodeTypeEnum.fileOutput]: (outputMap: FileMessage) => ({
    type: "file",
    name: outputMap.name,
    url: outputMap.url,
  }),
  [FlowNodeTypeEnum.videoOutput]: (outputMap: VideoMessage) => ({
    type: "video",
    name: outputMap.name,
    url: outputMap.url,
  }),
  [FlowNodeTypeEnum.imageOutput]: (outputMap: ImageMessage) => ({
    ...outputMap,
    type: "image",
  }),
  [FlowNodeTypeEnum.cardOutput]: (outputMap: CardMessage) => ({
    ...outputMap,
    type: "card",
  }),
  [FlowNodeTypeEnum.voiceOutput]: (outputMap: VoiceMessage) => ({
    ...outputMap,
    type: "voice",
  }),
  [FlowNodeTypeEnum.miniProgramOutput]: (outputMap: MiniProgramMessage) => ({
    ...outputMap,
    type: "miniProgram",
  }),
};

/**
 * n8n 编排机器人专用对话测试抽屉
 * <p>
 * 与共享 ChatDrawer 的区别（n8n 测试链路的固有语义）：
 * 1. SSE 端点固定为 ChatBot /n8nTest/start，不经内部 Flow 测试链路
 * 2. n8n 链路无任何完成事件，收到首条产出结束 loading，SSE 不断开以便后续产出继续渲染
 * 3. 触发前失败经契约外 errorMessage 事件展示；运行期失败无事件通道，loading 至连接关闭后结束
 * 4. 仅支持文本输入；无欢迎语；无「查看详情」（n8n 无按节点 stream 数据）
 * 渲染零件（气泡组件、输入框、消息类型）跨目录复用 ChatDrawer 目录内的纯渲染件
 */
export default function N8nChatDrawer({
  visible,
  onClose,
  _id,
  interruptOldOnNew = true,
  className,
}: N8nChatDrawerProps) {
  /* 会话与消息列表状态 */
  const [chatId, setChatId] = useState("");
  const [history, setHistory] = useState<ChatHistory[]>([]);
  const [value, setValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  /* 进行中的对话轮次：loading 消息 ID 与对应的中止控制器 */
  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  const [abortSignals, setAbortSignals] = useState<[string, AbortController][]>(
    []
  );

  const fetchUrl = `/api/damselfish-chat-bot/n8nTest/start/${_id}`;

  /**
   * 滚动消息列表到底部
   * 延迟执行以等待新气泡完成渲染
   */
  const scrollToBottom = (delay = 100) => {
    setTimeout(() => {
      containerRef.current?.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, delay);
  };

  /**
   * 结束一轮对话的 loading
   * 去掉 loading 并把对应用户消息标记为完成；不断开 SSE，后续产出仍可入列
   */
  const finishMessageRound = (updateId: string) => {
    setLoadingIds((state) => state.filter((id) => id !== updateId));
    setHistory(
      produce((state) => {
        const item = state.find((item) => item.id === updateId);
        if (item) {
          item.status = "done";
        }
      })
    );
  };

  /**
   * 将 SSE 产出事件渲染为 AI 气泡
   * 按 moduleInfo.flowType 命中映射表构造消息入列，未登记类型自然跳过
   */
  const handleMessage = (data: StreamResponseDataType) => {
    /* 只处理对话产出事件 */
    if (!data.dialog) return;
    const buildMsg = OUTPUT_MSG_BUILDERS[data.moduleInfo.flowType];
    if (!buildMsg) return;

    /* 构造气泡消息并入列 */
    setHistory(
      produce((state) => {
        state.push({
          role: "ai",
          status: "done",
          msg: buildMsg(data.moduleInfo.outputMap),
          id: nanoid(),
        });
      })
    );
  };

  /**
   * 打断全部仍存活的 SSE 连接
   * 中止连接并把进行中的消息标记为完成；首条产出后 loading 已空，仍以未释放连接为准
   */
  function handleStop() {
    /* 无未释放连接时无需处理 */
    if (abortSignals.length === 0) return;

    /* 标记进行中消息为完成并中止连接 */
    setHistory(
      produce((state) => {
        for (let i = 0; i < state.length; i++) {
          const item = state[i];
          if (item && loadingIds.includes(item.id)) {
            item.status = "done";
          }
        }
      })
    );
    abortSignals.forEach((s) => s[1].abort());
    setLoadingIds([]);
    setAbortSignals([]);
  }

  /**
   * 发送一轮 n8n 测试对话
   * 仅支持文本输入；连发时先打断上一轮；经 /n8nTest/start 建立 SSE 消费产出
   */
  async function handleAskAI(question: string, type: AskAIType) {
    /* 入参 fast-fail：n8n 测试对话仅支持文本输入 */
    if (type !== "TEXT") {
      message.warning("n8n 测试对话暂仅支持文本输入");
      return;
    }

    /* 2026-09-22 按仍存活的连接打断上一轮，避免首条产出清掉 loading 后旧 SSE 还叠着 */
    if (interruptOldOnNew && abortSignals.length > 0) {
      handleStop();
    }

    /* 用户消息入列并进入 loading */
    const updateId = nanoid();
    const abortSignal = new AbortController();
    setLoadingIds(
      produce((state) => {
        state.push(updateId);
      })
    );
    setAbortSignals(
      produce((state) => {
        state.push([updateId, abortSignal]);
      })
    );
    setHistory(
      produce((state) => {
        state.push({
          role: "human",
          status: "running",
          msg: { type: "text", messageOutput: question },
          id: updateId,
        });
      })
    );
    setValue("");
    scrollToBottom(300);

    /* 建立 SSE 消费 n8n 产出 */
    try {
      await streamFetch({
        url: fetchUrl,
        data: { userChatInput: question, chatId: chatId },
        onMessage(data) {
          scrollToBottom();

          /* 触发前失败事件：展示原因并结束本轮（事件体不在 StreamResponseDataType 契约内） */
          const n8nErrorEvent = data as N8nErrorEvent;
          if (!!n8nErrorEvent?.errorMessage && !n8nErrorEvent?.dialog) {
            message.error(n8nErrorEvent.errorMessage);
            finishMessageRound(updateId);
            return;
          }

          /* 2026-09-20 首条产出只结束 loading，不断开连接，后续回复继续画 */
          if (!!data?.dialog) {
            finishMessageRound(updateId);
            handleMessage(data);
          }
        },
        onClose() {
          finishMessageRound(updateId);
          setAbortSignals((state) => state.filter(([id]) => id !== updateId));
        },
        abortCtrl: abortSignal,
      });

      /* n8n 无 done 事件，promise 常态悬挂至连接关闭；此行仅作兜底 */
      finishMessageRound(updateId);
      setAbortSignals((state) => state.filter(([id]) => id !== updateId));
    } catch (err) {
      message.error((err as Error).message);
      finishMessageRound(updateId);
      setAbortSignals((state) => state.filter(([id]) => id !== updateId));
    }
  }

  /**
   * 抽屉开关切换处理
   * 打开时生成新会话 ID；关闭时打断进行中轮次并清空消息列表
   */
  const handleOpenChange = (open: boolean) => {
    /* 打开：生成新会话 ID */
    if (open) {
      setChatId(`${_id}-${nanoid()}`);
      return;
    }

    /* 关闭：打断并清空 */
    handleStop();
    setHistory([]);
  };

  /**
   * 清空 n8n 测试对话记录
   * 先打断进行中的轮次，再调后端清空接口，成功后同步清空本地列表
   */
  const handleClearHistory = async () => {
    /* 仍存活的 SSE 先打断 */
    handleStop();

    /* 调后端清空接口，成功后同步清空本地列表 */
    const rsp = await clearN8nTestHistoryService(chatId);
    if (rsp.code !== ResponseCode.Success) {
      message.error(rsp.msg);
      return;
    }
    message.success("清除成功");
    setHistory([]);
  };

  return (
    <Drawer
      mask={false}
      maskClosable={false}
      onClose={onClose}
      open={visible}
      closable
      width={500}
      destroyOnClose
      afterOpenChange={handleOpenChange}
      title={
        <Row justify="space-between" align="middle">
          <span className={styles.title}>对话调试</span>
          <Space>
            <Button
              type="text"
              icon={<ClearIcon />}
              onClick={handleClearHistory}
            />
          </Space>
        </Row>
      }
      rootClassName={classNames(styles.drawer, className)}
    >
      <div className={styles.main}>
        <div className={styles.content} ref={containerRef}>
          {history.map((item) => (
            <Fragment key={item.id}>
              {item.role === "human" && (
                <ChatItemHuman
                  msg={item.msg!}
                  loading={item.status === "running"}
                  onRefresh={() => {
                    if (item.msg!.type === "text") {
                      handleAskAI(item.msg!.messageOutput, "TEXT");
                    }
                  }}
                  onViewDetail={() => {
                    /* n8n 链路无按节点 stream 数据，不提供详情 */
                  }}
                  onDelete={() => {
                    /* TODO: 删除 */
                  }}
                />
              )}

              {item.role === "ai" && (
                <ChatItemAI
                  msg={item.msg!}
                  onDelete={() => {
                    /* TODO: 删除 */
                  }}
                />
              )}
            </Fragment>
          ))}
        </div>
        <ChatInput
          value={value}
          onChange={(v) => setValue(v)}
          onEnter={handleAskAI}
        />
      </div>
    </Drawer>
  );
}
